import { forwardRef, type ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * RwLozenge — the DS Lozenge (rw-ds.css `.rw-lozenge`).
 *
 * Read-only status indicator. **Status hues ONLY** (success | warning | critical |
 * information | neutral) — for category / type labels use `RwTag`. Not interactive,
 * not dismissable. Anatomy + tokens: Storybook → Components → Lozenge; governance →
 * rw-ds-components § Lozenge + rw-ds-color-usage.
 *
 * `sx` is layout-only — never for on-DS visual overrides (fill/text/border come from
 * the `.rw-lozenge` CSS).
 */
export type RwLozengeTone = 'success' | 'warning' | 'critical' | 'information' | 'neutral';

export interface RwLozengeProps {
  tone?: RwLozengeTone;
  /** default = subtle fill; `bold` = reverse-out fill (per rw-ds.css). */
  emphasis?: 'subtle' | 'bold';
  size?: 'sm' | 'md';
  /** Leading 6px status dot (`.rw-lozenge__icon`). Ignored when `icon` is set. */
  dot?: boolean;
  /** A Material icon node as the leading glyph (16px, sized by CSS). */
  icon?: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
}

const RwLozenge = forwardRef<HTMLSpanElement, RwLozengeProps>(function RwLozenge(
  { tone = 'neutral', emphasis = 'subtle', size = 'md', dot = false, icon, className, sx, children },
  ref
) {
  const classes = [
    'rw-lozenge',
    `rw-lozenge--${tone}`,
    emphasis === 'bold' ? 'rw-lozenge--bold' : '',
    size === 'sm' ? 'rw-lozenge--sm' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box component="span" ref={ref} className={classes} sx={sx}>
      {icon ?? (dot ? <span className="rw-lozenge__icon" /> : null)}
      {children}
    </Box>
  );
});

export default RwLozenge;
